import { InjectionToken } from '@angular/core';
import { PeriodDepreciationCalculated } from '@assetaccountant/api/models/period-depreciation-calculated';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { assertNever } from './asset-utils';

export const TODAY = new InjectionToken<string>('TODAY');

const PREFERRED_FORMAT = 'YYYY-MM-DD';
export const ACCEPTED_FORMATS = [PREFERRED_FORMAT, moment.ISO_8601];
export const SENTINEL_DATE = '9999-12-31';

/// Should match results from server from AsDateReportString_NoSpecifiedCultureTest
const LOCALE_DATE_FORMATS = {
  'en-CA': 'DMY',

  'en-US': 'MDY',
};

const SHORT_DATE_FORMATS = {
  DMY: 'dd/MM/yyyy',
  MDY: 'MM/dd/yyyy',
  YMD: 'yyyy/MM/dd',
};

const MEDIUM_DATE_FORMATS = {
  DMY: 'D MMM YYYY',
  MDY: 'MMM D YYYY',
  YMD: 'YYYY MMM D',
};

export const now = () => moment().format(PREFERRED_FORMAT);

export function isMinDate(value: string) {
  return value.split(/T/)[0] === '0001-01-01';
}

export function toDateValue(value: string | Date): Date {
  return moment(value).toDate();
}

function isNgbDateStruct(value: any): value is NgbDateStruct {
  return value && 'year' in value && 'month' in value && 'day' in value;
}

export const parseDate = (value: string | null | undefined, locale: string) => {
  const format = getShortDateFormat(locale).toUpperCase();
  const shortFormat = format.replace('DD', 'D').replace('MM', 'M').replace('YYYY', 'YY');

  // Create dash-separated formats as well as slash-separated
  const dashFormat = format.replace(/\//g, '-');
  const dashShortFormat = shortFormat.replace(/\//g, '-');

  const mom =
    value &&
    moment.utc(value, [format, shortFormat, dashFormat, dashShortFormat, ...ACCEPTED_FORMATS]);

  return mom && mom.isValid() ? mom.format(PREFERRED_FORMAT) : null;
};

export const toUniversalDateString = ((
  value: moment.Moment | NgbDateStruct | string | null | undefined,
  options?: { throwOnFailure?: boolean },
) => {
  let mom: moment.Moment | undefined;

  if (moment.isMoment(value)) {
    mom = value;
  } else if (typeof value === 'string') {
    mom = moment.utc(value, ACCEPTED_FORMATS);
  } else if (isNgbDateStruct(value)) {
    const dateStr = [value.year, value.month, value.day].join('-');
    mom = moment.utc(dateStr, ['YYYY-M-D', 'YY-M-D'], true);
  }

  if (mom && mom.isValid()) {
    return mom.format(PREFERRED_FORMAT);
  } else {
    if (options?.throwOnFailure) {
      throw new Error(
        `Could not convert date object to universal date. Value = ${value?.toString()}`,
      );
    }
    if (value && !('__karma__' in window)) {
      console.warn(`Could not convert date object to universal date. Value = ${value.toString()}`);
    }
    return null;
  }
}) as {
  (
    value: moment.Moment | NgbDateStruct | string | null | undefined,
    options: { throwOnFailure: true },
  ): string;
  (
    value: moment.Moment | NgbDateStruct | string | null | undefined,
    options?: { throwOnFailure?: false },
  ): string | null;
};

export function toReportingYearEndDateString(startMonth: number, startDay: number): string {
  const startDate = moment()
    .month(startMonth - 1)
    .date(startDay);
  const endDate = moment(startDate).add(1, 'years').add(-1, 'days');
  let endDateFormat = moment(endDate).format('D MMMM');
  if (endDate.month() + 1 === 2 && (endDate.date() === 28 || endDate.date() === 29)) {
    endDateFormat = '28/29 February';
  }
  return `${moment(startDate).format('D MMMM')} → ${endDateFormat}`;
}

export function utcToLocalDate(value: string, format: string) {
  return moment(value).format(format);
}

export function getLocaleDateString(locale: string): string {
  return LOCALE_DATE_FORMATS[locale] || 'DMY';
}

export function getShortDateFormat(locale: string): string {
  return SHORT_DATE_FORMATS[getLocaleDateString(locale)] || SHORT_DATE_FORMATS.DMY;
}

export function getMediumDateFormat(locale: string): string {
  return MEDIUM_DATE_FORMATS[getLocaleDateString(locale)] || MEDIUM_DATE_FORMATS.DMY;
}

export function compareProximityToDate(refDate: string): (a: string, b: string) => number;
export function compareProximityToDate<TKey extends string>(
  refDate: string,
  key: TKey,
): (a: { [k in TKey]: string }, b: { [k in TKey]: string }) => number;
export function compareProximityToDate<TKey extends string | undefined>(
  refDate: string,
  key?: TKey,
) {
  const mom = moment.utc(refDate);

  const selector = key ? x => x[key] ?? '' : x => x;

  return (a, b) => {
    if (a === b) {
      return 0;
    }
    return Math.abs(mom.diff(selector(a), 'days')) - Math.abs(mom.diff(selector(b), 'days'));
  };
}

export function getFinancialYearContaining(date: string, referenceStartDate: string) {
  let startDate = date.substring(0, 4) + referenceStartDate.substring(4, 10);
  if (startDate > date.substring(0, 10)) {
    startDate = startDate.replace(/^\d{4}\b/, year => (+year - 1).toString());
  }

  const endDate = new Date(startDate);
  endDate.setFullYear(endDate.getFullYear() + 1);
  endDate.setDate(endDate.getDate() - 1);

  return {
    startDate,
    endDate: toUniversalDateString(endDate.toISOString(), { throwOnFailure: true }),
  };
}

// Copy of API logic for PeriodDepreciationCalculated
export function addYears(
  periodDepreciationCalculation: PeriodDepreciationCalculated,
  date: string,
  years: number,
  referenceStart: { month: number; day: number },
) {
  switch (periodDepreciationCalculation) {
    case PeriodDepreciationCalculated.Monthly:
      return toUniversalDateString(moment.utc(date).add(Math.round(years * 12), 'months'), {
        throwOnFailure: true,
      });

    case PeriodDepreciationCalculated.Daily_DaysAreEqual:
      return toUniversalDateString(moment.utc(date).add(Math.round(years * 365), 'days'), {
        throwOnFailure: true,
      });

    case PeriodDepreciationCalculated.Daily_YearsAreEqual:
      return addFractionalYears(moment.utc(date), years, referenceStart);

    case PeriodDepreciationCalculated.Yearly:
      throw new Error('Yearly not supported');

    default:
      assertNever(periodDepreciationCalculation, 'Unexpected PeriodDepreciationCalculated');
  }
}

export function differenceInDays(laterExclusive: string, earlierInclusive: string) {
  const laterExclusiveDate = toNativeDate(laterExclusive);
  const earlierInclusiveDate = toNativeDate(earlierInclusive);

  return Math.round(
    (laterExclusiveDate.valueOf() - earlierInclusiveDate.valueOf()) / 1000 / 60 / 60 / 24,
  );
}

function toNativeDate(dateString: string) {
  const [_, year, month, day] = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString) ?? [];

  if (!year || !month || !day) {
    throw new Error('Invalid date');
  }

  return new Date(+year, +month - 1, +day);
}

// Copy of API logic for PeriodDepreciationCalculated
export function differenceInYears(
  periodDepreciationCalculation: PeriodDepreciationCalculated,
  laterExclusive: string,
  earlierInclusive: string,
  referenceStart: { month: number; day: number },
) {
  // Copy of API logic for PeriodDepreciationCalculated
  switch (periodDepreciationCalculation) {
    case PeriodDepreciationCalculated.Monthly: {
      const years = +laterExclusive.substring(0, 4) - +earlierInclusive.substring(0, 4);
      const months = +laterExclusive.substring(5, 7) - +earlierInclusive.substring(5, 7);
      return years + months / 12;
    }

    case PeriodDepreciationCalculated.Daily_DaysAreEqual:
      return differenceInDays(laterExclusive, earlierInclusive) / 365;

    case PeriodDepreciationCalculated.Daily_YearsAreEqual:
      return differenceInFractionalYears(
        moment.utc(laterExclusive),
        moment.utc(earlierInclusive),
        referenceStart,
      );

    case PeriodDepreciationCalculated.Yearly:
      throw new Error('Yearly not supported');

    default:
      assertNever(periodDepreciationCalculation, 'Unexpected PeriodDepreciationCalculated');
  }
}

export function isSentinelDate(date: string | null | undefined): boolean {
  return date === SENTINEL_DATE;
}

/**
 * Calculates a finance term string (inclusive) between two dates.
 * Logic mirrors C# DateOnly.AddMonths behavior (clamping end-of-month).
 */
export function getFinanceTerm(startDate: Date, endDate: Date): string {
  // 1. Clone inputs to avoid mutating originals and strip time components
  let current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  const target = new Date(endDate);
  target.setHours(0, 0, 0, 0);

  // 2. Inclusive Logic: Add 1 day to the end date
  target.setDate(target.getDate() + 1);

  // Guard: If start > end (after inclusive adjustment), return generic or throw
  if (current.getTime() > target.getTime()) {
    return '0 days';
  }

  const parts: string[] = [];

  // --- Calculate Years ---
  let years = 0;
  while (true) {
    const nextYear = addMonthsSafe(current, (years + 1) * 12);
    if (nextYear.getTime() <= target.getTime()) {
      years++;
    } else {
      break;
    }
  }

  if (years > 0) {
    // Advance current by the calculated years
    current = addMonthsSafe(current, years * 12);
    parts.push(years === 1 ? '1 year' : `${years} years`);
  }

  // --- Calculate Months ---
  let months = 0;
  while (true) {
    const nextMonth = addMonthsSafe(current, months + 1);
    if (nextMonth.getTime() <= target.getTime()) {
      months++;
    } else {
      break;
    }
  }

  if (months > 0) {
    // Advance current by the calculated months
    current = addMonthsSafe(current, months);
    parts.push(months === 1 ? '1 month' : `${months} months`);
  }

  // --- Calculate Days ---
  // Calculate difference in milliseconds and convert to days
  const diffTime = target.getTime() - current.getTime();
  const days = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (days > 0) {
    parts.push(days === 1 ? '1 day' : `${days} days`);
  }

  return parts.length > 0 ? parts.join(' ') : '0 days';
}

// Copy of API logic for PeriodDepreciationCalculated
function addFractionalYears(
  date: moment.Moment,
  years: number,
  referenceStart: { month: number; day: number },
) {
  const startOfCurrentYear = moment.utc({
    year: date.year(),
    month: referenceStart.month - 1,
    date: referenceStart.day,
  });

  if (startOfCurrentYear.isAfter(date)) {
    startOfCurrentYear.add(-1, 'year');
  }

  let remainingYears = years;

  while (true) {
    if (remainingYears <= 0) {
      return toUniversalDateString(startOfCurrentYear, {
        throwOnFailure: true,
      });
    }

    const startOfNextYear = startOfCurrentYear.clone().add(1, 'year');
    const daysInYear = startOfNextYear.diff(startOfCurrentYear, 'days');
    const daysOfYear = startOfCurrentYear.isSameOrBefore(date)
      ? startOfNextYear.diff(date, 'days')
      : daysInYear;
    const portionOfYear = daysOfYear / daysInYear;

    if (remainingYears <= portionOfYear) {
      return toUniversalDateString(
        startOfCurrentYear.clone().add(Math.round(remainingYears * daysInYear), 'days'),
        {
          throwOnFailure: true,
        },
      );
    }

    remainingYears -= portionOfYear;
    startOfCurrentYear.add(1, 'year');
  }
}

// Copy of API logic for PeriodDepreciationCalculated
function differenceInFractionalYears(
  laterExclusive: moment.Moment,
  earlierInclusive: moment.Moment,
  referenceStart: { month: number; day: number },
) {
  const startOfCurrentYear = moment.utc({
    year: earlierInclusive.year(),
    month: referenceStart.month - 1,
    date: referenceStart.day,
  });

  if (startOfCurrentYear.isAfter(earlierInclusive)) {
    startOfCurrentYear.add(-1, 'year');
  }

  let result = 0;

  while (startOfCurrentYear.isSameOrBefore(laterExclusive)) {
    const startOfNextYear = startOfCurrentYear.clone().add(1, 'year');
    const daysInYear = startOfNextYear.diff(startOfCurrentYear, 'days');

    if (startOfCurrentYear.isSameOrBefore(earlierInclusive)) {
      if (startOfNextYear.isAfter(laterExclusive)) {
        return moment.utc(laterExclusive).diff(earlierInclusive, 'days') / daysInYear;
      }

      result += startOfNextYear.diff(earlierInclusive, 'days') / daysInYear;
    } else if (startOfNextYear.isAfter(laterExclusive)) {
      result += moment.utc(laterExclusive).diff(startOfCurrentYear, 'days') / daysInYear;
    } else {
      result += 1;
    }

    startOfCurrentYear.add(1, 'year');
  }

  return result;
}

/**
 * Helper to replicate C# AddMonths logic.
 * JS native setMonth() overflows (Jan 31 + 1 month = March 3).
 * This clamps (Jan 31 + 1 month = Feb 28/29).
 */
function addMonthsSafe(date: Date, monthsToAdd: number): Date {
  const newDate = new Date(date);
  const originalDay = newDate.getDate();

  // Set the first day of the month to avoid overflow immediately
  newDate.setDate(1);
  newDate.setMonth(newDate.getMonth() + monthsToAdd);

  // Find the last day of this new month
  // (Day 0 of the *next* month is the last day of *this* month)
  const lastDayOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();

  // Clamp date: if original day (e.g. 31) is > last day (e.g. 28), use 28.
  newDate.setDate(Math.min(originalDay, lastDayOfMonth));

  return newDate;
}

/**
 * Returns the latest of the given ISO date strings (YYYY-MM-DD), ignoring null/undefined.
 * Returns null if no valid dates were supplied.
 */
export function pickLatestDate(...dates: (string | null | undefined)[]): string | null {
  let latest: string | null = null;
  for (const date of dates) {
    if (date && (latest === null || date > latest)) {
      latest = date;
    }
  }
  return latest;
}
